import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MapPin, Calendar } from 'lucide-react';
import { formatDate } from '../../../hooks/utils';

/**
 * Post Context Card — shown at the top of a message thread to give
 * both participants a visual reminder of the post that started the
 * conversation.
 */
const PostContextCard = ({ 
  postId, 
  postType, 
  postName, 
  postBreed, 
  postImageUrl, 
  postLocation, 
  postDate 
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  if (!postId && !postName) return null;

  const getTypeBadge = () => {
    switch (postType) {
      case 'lost':
        return { label: t('lost', 'Lost'), className: 'bg-red-100 text-red-700' };
      case 'found':
        return { label: t('found', 'Found'), className: 'bg-green-100 text-green-700' };
      case 'petService':
        return { label: t('pet_service', 'Service'), className: 'bg-purple-100 text-purple-700' };
      case 'psa':
        return { label: t('psa_short', 'PSA'), className: 'bg-blue-100 text-blue-700' };
      default:
        return null;
    }
  };

  const badge = getTypeBadge();

  const handleCardClick = () => {
    if (postId) {
      navigate(`/post/${postId}`, {
        state: { postType },
      });
    }
  };

  return (
    <div 
      onClick={handleCardClick}
      className="bg-white mx-3 sm:mx-4 mt-3 sm:mt-4 rounded-xl border border-border overflow-hidden cursor-pointer hover:shadow-sm transition-shadow"
    >
      <div className="flex gap-2.5 sm:gap-3 p-2.5 sm:p-3">
        {/* Post Image */}
        {postImageUrl ? (
          <img 
            src={postImageUrl} 
            alt={postName || 'Post'} 
            className="w-14 h-14 sm:w-16 sm:h-16 rounded-lg object-cover flex-shrink-0" 
          />
        ) : (
          <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-lg bg-secondary flex items-center justify-center flex-shrink-0">
            <span className="text-xl sm:text-2xl">🐾</span>
          </div>
        )}

        {/* Post Details */}
        <div className="flex-1 min-w-0">
          {/* Name + Badge */}
          <div className="flex items-center gap-1.5 sm:gap-2 mb-0.5 sm:mb-1">
            {postName && (
              <h3 className="font-semibold text-foreground text-xs sm:text-sm truncate">
                {postName}
              </h3>
            )}
            {badge && (
              <span className={`text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 rounded-full flex-shrink-0 font-medium ${badge.className}`}>
                {badge.label}
              </span>
            )}
          </div>

          {/* Breed */}
          {postBreed && (
            <p className="text-[10px] sm:text-xs text-muted-foreground mb-1 sm:mb-1.5 truncate">
              {postBreed}
            </p>
          )}

          {/* Location & Date */}
          <div className="flex flex-col gap-0.5 sm:gap-1 text-[10px] sm:text-xs text-muted-foreground">
            {postLocation && (
              <span className="flex items-center gap-1 truncate">
                <MapPin className="w-2.5 h-2.5 sm:w-3 sm:h-3 flex-shrink-0" />
                {postLocation}
              </span>
            )}
            {postDate && (
              <span className="flex items-center gap-1">
                <Calendar className="w-2.5 h-2.5 sm:w-3 sm:h-3 flex-shrink-0" />
                {formatDate(postDate)}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PostContextCard;

